// Gamepad support, polled from the rAF loop (the Gamepad API has no events
// for sticks). In play: left stick / d-pad flies, A · RB · RT beams, X · LB
// deploys the held special, Start
// pauses. In menus: d-pad or stick moves the highlight, A selects, B cancels,
// Start presses the default action. Covers USB pads and gamestr-arcade
// cabinets that expose their controls as a HID gamepad.

import { setGamepadState } from './input';
import { navMove, navActivate, navCancel, navStart, uiActive, showFocus } from './menunav';

const DEADZONE = 0.22;
const UI_TILT = 0.5;

export interface GamepadHooks {
  onConnect(): void;
  onPauseToggle(): void;
  /** Any button edge — used to unlock audio the way a key press would. */
  onActivity(): void;
}

let hooks: GamepadHooks | null = null;
let prev: boolean[] = [];
let prevDirs = { up: false, down: false, left: false, right: false };
let focused = true;
let requireNeutral = true;
let padPresent = false;

function clearPadState(): void {
  prev.length = 0;
  prevDirs.up = false;
  prevDirs.down = false;
  prevDirs.left = false;
  prevDirs.right = false;
  setGamepadState(0, 0, false, false);
}

function captureButtons(p: Gamepad): void {
  prev.length = p.buttons.length;
  for (let i = 0; i < p.buttons.length; i++) prev[i] = p.buttons[i].pressed;
}

function suspendPad(): void {
  focused = false;
  requireNeutral = true;
  clearPadState();
}

function resumePad(): void {
  focused = true;
  requireNeutral = true;
  clearPadState();
}

export function initGamepad(h: GamepadHooks): void {
  hooks = h;
  window.addEventListener('gamepadconnected', () => {
    padPresent = true;
    requireNeutral = true;
    clearPadState();
    showFocus();
    h.onConnect();
  });
  window.addEventListener('gamepaddisconnected', () => {
    padPresent = false;
    requireNeutral = true;
    clearPadState();
  });
  window.addEventListener('blur', suspendPad);
  window.addEventListener('focus', resumePad);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) suspendPad();
    else resumePad();
  });
}

function firstPad(): Gamepad | null {
  if (!navigator.getGamepads) return null;
  for (const p of navigator.getGamepads()) {
    if (p && p.connected) return p;
  }
  return null;
}

export function pollGamepad(): void {
  if (!focused || document.hidden) {
    clearPadState();
    return;
  }
  const p = firstPad();
  if (!p) {
    if (padPresent || prev.length > 0) clearPadState();
    padPresent = false;
    requireNeutral = true;
    return;
  }
  padPresent = true;
  const held = (i: number) => p.buttons[i]?.pressed ?? false;
  const rose = (i: number) => held(i) && !prev[i];

  let x = p.axes[0] ?? 0;
  let z = p.axes[1] ?? 0;
  if (requireNeutral) {
    setGamepadState(0, 0, false, false);
    captureButtons(p);
    prevDirs.up = false;
    prevDirs.down = false;
    prevDirs.left = false;
    prevDirs.right = false;
    if (Math.hypot(x, z) < DEADZONE && p.buttons.every((b) => !b.pressed)) {
      requireNeutral = false;
    }
    return;
  }
  if (hooks && p.buttons.some((b, i) => b.pressed && !prev[i])) hooks.onActivity();

  if (Math.hypot(x, z) < DEADZONE) { x = 0; z = 0; }
  if (held(14)) x -= 1;
  if (held(15)) x += 1;
  if (held(12)) z -= 1;
  if (held(13)) z += 1;
  const len = Math.hypot(x, z);
  if (len > 1) { x /= len; z /= len; }

  if (uiActive()) {
    setGamepadState(0, 0, false, false);
    const dirs = { up: z < -UI_TILT, down: z > UI_TILT, left: x < -UI_TILT, right: x > UI_TILT };
    for (const d of ['up', 'down', 'left', 'right'] as const) {
      if (dirs[d] && !prevDirs[d]) navMove(d);
    }
    prevDirs = dirs;
    if (rose(0)) navActivate();
    if (rose(1)) navCancel();
    if (rose(9)) navStart();
  } else {
    prevDirs = { up: false, down: false, left: false, right: false };
    setGamepadState(x, z, held(0) || held(5) || held(7), held(2) || held(4));
    if (rose(9)) hooks?.onPauseToggle();
  }
  captureButtons(p);
}
